<template>
  <div v-if="year" class="offsite-year-page">

    <section class="year-intro">
      <div class="year-inner">
        <h1 class="year-title">
          <span class="city">{{ year.city }}</span>
          <span class="yr">{{ year.year }}</span>
        </h1>
        <img :src="year.heroImage" :alt="`ORE OFFSITE ${year.city} ${year.year}`" class="hero-img" />
      </div>
    </section>

    <section class="year-details">
      <div class="year-inner">
        <div class="details-row">
          <div class="details-left">
            <div class="detail-block">
              <span class="detail-label">Location</span>
              <p class="detail-name">{{ year.location.name }}</p>
              <p v-if="year.location.description" class="detail-desc">{{ year.location.description }}</p>
            </div>
          </div>
          <div class="details-right">
            <div v-for="h in year.highlights" :key="h.label" class="detail-block">
              <span class="detail-label">{{ h.label }}</span>
              <p class="detail-name">{{ h.name }}</p>
              <p v-if="h.description" class="detail-desc">{{ h.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <template v-if="year.panel?.length">
      <div class="year-divider"><div class="year-inner"><hr /></div></div>
      <section class="year-panel">
        <div class="year-inner">
          <div class="details-row">
            <div class="details-left">
              <span class="detail-label">Panel Discussion</span>
            </div>
            <div class="details-right">
              <div v-for="s in year.panel" :key="s.name" class="speaker">
                <p class="speaker-name">{{ s.name }}</p>
                <p class="speaker-title">{{ s.title }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <div class="year-divider"><div class="year-inner"><hr /></div></div>

    <section class="prev-offsites">
      <div class="year-inner">
        <h2 class="prev-heading">Previous OFFSITES</h2>
        <div class="prev-grid">
          <router-link
            v-for="other in otherYears"
            :key="other.slug"
            :to="`/${other.slug}`"
            class="prev-card"
          >
            <img :src="other.cardImage" :alt="`${other.city} ${other.year}`" />
            <p>{{ other.city }}<br />{{ other.year }}</p>
          </router-link>
        </div>
      </div>
    </section>

    <div class="year-back">
      <router-link to="/offsite" class="back-btn">Back to ORE OFFSITE</router-link>
    </div>

  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { offsiteYears } from '@/data/offsiteYears.js'

const props = defineProps({ yearSlug: String })
const route = useRoute()

const slug = computed(() => props.yearSlug || route.path.replace(/^\//, '').replace(/\/$/, ''))
const year = computed(() => offsiteYears.find(y => y.slug === slug.value))
const otherYears = computed(() => offsiteYears.filter(y => y.slug !== slug.value))
</script>

<style lang="scss" scoped>
$orange: #e8521a;

.offsite-year-page {
  background: #fff;
  min-height: 100vh;
  padding-top: 80px;
}

.year-inner {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 32px;
}

.year-intro {
  padding: 48px 0 0;

  .year-title {
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 400;
    line-height: 1.1;
    margin-bottom: 32px;
    color: #111;

    .city { font-weight: 400; }
    .yr   { font-weight: 300; margin-left: 0.25em; }
  }

  .hero-img {
    display: block;
    width: 100%;
    height: auto;
  }
}

.year-details {
  padding: 48px 0 0;
}

.details-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.detail-block {
  margin-bottom: 28px;
}

.detail-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 6px;
}

.detail-name {
  font-size: 1.05rem;
  font-weight: 500;
  color: #111;
  margin: 0 0 4px;
}

.detail-desc {
  font-size: 0.9rem;
  color: #555;
  font-style: italic;
  margin: 0;
  line-height: 1.5;
}

.year-divider {
  padding: 32px 0 0;
  hr {
    border: none;
    border-top: 1px solid #e0e0e0;
    margin: 0;
  }
}

.year-panel {
  padding: 40px 0 0;

  .speaker {
    margin-bottom: 20px;
  }

  .speaker-name {
    font-size: 1rem;
    font-weight: 500;
    color: #111;
    margin: 0 0 2px;
  }

  .speaker-title {
    font-size: 0.88rem;
    color: #666;
    font-style: italic;
    margin: 0;
    line-height: 1.4;
  }
}

.prev-offsites {
  padding: 56px 0 0;

  .prev-heading {
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    font-weight: 400;
    text-align: center;
    margin-bottom: 40px;
    color: #111;
  }

  .prev-grid {
    // Column auto-flow keeps this to a single row whatever the year count is,
    // so adding an OFFSITE never pushes the strip onto a second line.
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    gap: 24px;

    @media (max-width: 768px) {
      grid-auto-flow: row;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    @media (max-width: 480px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .prev-card {
    text-decoration: none;
    color: #111;
    display: block;

    img {
      display: block;
      width: 100%;
      aspect-ratio: 1;
      object-fit: cover;
      margin-bottom: 12px;
      transition: opacity 0.2s;
    }

    p {
      font-size: 0.9rem;
      font-weight: 500;
      line-height: 1.3;
      margin: 0;
    }

    &:hover img { opacity: 0.8; }
  }
}

.year-back {
  padding: 48px 0 64px;
  text-align: center;

  .back-btn {
    display: inline-block;
    background: $orange;
    color: #fff;
    text-decoration: none;
    padding: 14px 32px;
    font-size: 1rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    transition: opacity 0.2s;

    &:hover { opacity: 0.85; }
  }
}
</style>
